import React, { useState, useEffect } from "react";
import { communityServicesAPI } from "../../../services/api";

function ProgramsReport() {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [programsData, setProgramsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState(null);
  const [selectedProgramDetails, setSelectedProgramDetails] = useState(null);

  const fetchProgramReports = async () => {
    try {
      setLoading(true);
      const response = await communityServicesAPI.getProgramReports();

      if (response.data.success) {
        setProgramsData(response.data.data.program_reports || []);
      }
    } catch (error) {
      setError("Failed to fetch program reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgramReports();
    const interval = setInterval(fetchProgramReports, 300000);
    return () => clearInterval(interval);
  }, []);

  const completedPrograms = programsData.filter(
    (program) => program.status === "completed"
  );

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "Invalid Date";
    try {
      const isoString = dateTimeString.replace(" ", "T");
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return "Invalid Date";
      const month = date.toLocaleString("en-US", { month: "short" });
      const day = date.getDate();
      const year = date.getFullYear();
      const time = date.toLocaleString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${month} ${day}, ${year}, ${time}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const filteredData = completedPrograms.filter(
    (program) =>
      program.program_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const currentData = filteredData.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleView = (program) => {
    setSelectedProgramDetails(program);
    setSelectedProgramId(program.id || program.program_id || program._id);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProgramId(null);
    setSelectedProgramDetails(null);
  };

  const handleExport = () => {
    if (!filteredData.length) {
      alert("No data available to export.");
      return;
    }

    const headers = [
      "Program Name",
      "Date & Time",
      "Description",
      "Volunteers",
      "Participants",
      "Status",
    ];

    const rows = filteredData.map((program) => [
      `"${program.program_name.replace(/"/g, '""')}"`,
      `"${formatDateTime(program.start_date)}"`,
      `"${(program.description || "").replace(/"/g, '""')}"`,
      `"${(program.volunteers || []).join(", ").replace(/"/g, '""')}"`,
      `"${program.total_volunteers}"`,
      `"Completed"`,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `Completed_Programs_Report_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-50">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6 text-blue-600"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Completed Programs</h3>
            <p className="text-sm text-gray-600">
              <strong>{completedPrograms.length}</strong> program(s) completed in total
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
          Export
        </button>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search programs..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5 absolute left-3 top-2.5 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
        </div>
      </div>

      {/* Table */}
      <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Program</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Date & Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">Volunteers</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Participants</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading programs...</td>
                </tr>
              ) : currentData.length > 0 ? (
                currentData.map((program, index) => (
                  <tr key={program.id || index} className="hover:bg-gray-50">
                    {/* Program Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#4F46E5" className="size-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
                          </svg>
                        </div>
                        <span className="text-sm font-medium text-gray-900 truncate" title={program.program_name}>
                          {truncateText(program.program_name, 25)}
                        </span>
                      </div>
                    </td>

                    {/* Date & Time */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900">{formatDateTime(program.start_date)}</div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 truncate max-w-xs" title={program.description}>
                        {truncateText(program.description, 40)}
                      </div>
                    </td>

                    {/* Volunteers */}
                    <td className="px-4 py-3">
                      <div className="flex -space-x-1">
                        {program.volunteers && program.volunteers.slice(0, 3).map((volunteer, idx) => (
                          <div key={idx} className="w-5 h-5 rounded-full bg-blue-500 border border-white flex items-center justify-center text-xs text-white" title={volunteer}>
                            {volunteer.split(" ").map(n => n[0]).join("")}
                          </div>
                        ))}
                        {program.volunteers && program.volunteers.length > 3 && (
                          <div className="w-5 h-5 rounded-full bg-gray-300 border border-white flex items-center justify-center text-xs text-gray-600" title={`${program.volunteers.length - 3} more`}>
                            +{program.volunteers.length - 3}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Participants */}
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-900 text-center">{program.total_volunteers}</div>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Completed
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex justify-center">
                        <button
                          onClick={() => handleView(program)}
                          className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded-full transition-colors"
                          title="View Details"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {error ? error : "No completed programs found matching your search."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 bg-white border-t border-gray-300 gap-3">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{totalItems === 0 ? 0 : startIndex + 1}</span> to{" "}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{" "}
              <span className="font-medium">{totalItems}</span> results
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`p-1.5 rounded border ${currentPage === 1 ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title="First Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m18.75 4.5-7.5 7.5 7.5 7.5m-6-15L5.25 12l7.5 7.5" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`p-1.5 rounded border ${currentPage === 1 ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title="Previous"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                </svg>
              </button>

              <span className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded border ${currentPage === totalPages ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title="Next"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`p-1.5 rounded border ${currentPage === totalPages ? "text-gray-400 border-gray-300 cursor-not-allowed" : "text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                title="Last Page"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m5.25 4.5 7.5 7.5-7.5 7.5" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Program Details Modal */}
      {isModalOpen && (
        <ProgramDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          programData={selectedProgramDetails}
        />
      )}
    </div>
  );
}

const ProgramDetailsModal = ({ isOpen, onClose, programData }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    const date = new Date(dateTimeString);
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    const time = date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `${month} ${day}, ${year}, ${time}`;
  };

  const program = programData || {};

  let volunteers = [];
  if (Array.isArray(program.volunteers)) {
    volunteers = program.volunteers;
  } else if (program.volunteers && typeof program.volunteers === 'string') {
    volunteers = program.volunteers.split(',').map(v => v.trim());
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Program Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Program Name</h3>
                <p className="text-lg font-semibold text-gray-900">{program.program_name || program.name || 'No program name'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Date & Time</h3>
                <p className="text-base text-gray-900">{program.start_date ? formatDateTime(program.start_date) : 'No date specified'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Location</h3>
                <p className="text-base text-gray-900">{program.location || 'No location specified'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                <p className="text-base text-gray-900">{program.description || 'No description'}</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Organizer</h3>
                <p className="text-base text-gray-900">Organization Name</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Assigned Volunteers</h3>
                <p className="text-base text-gray-900 mb-4">
                  {volunteers.length} volunteer(s)
                </p>

                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {volunteers.length > 0 ? (
                    volunteers.map((volunteer, idx) => {
                      const volunteerName = typeof volunteer === 'string' ? volunteer : volunteer.full_name || volunteer.name || 'Volunteer';
                      const volunteerId = typeof volunteer === 'object' ? volunteer.barangay_id_no || volunteer.id : '-';
                      const volunteerRole = typeof volunteer === 'object' ? volunteer.role : 'Volunteer';

                      return (
                        <div key={idx} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-xs font-medium text-white">
                                {volunteerName?.split(' ').map(n => n[0]).join('').toUpperCase() || 'V'}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {volunteerName}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span>{volunteerId}</span>
                                <span>•</span>
                                <span>{volunteerRole}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm">No assigned volunteers.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramsReport;